/**
 * KOPS-461: Contact Intelligence Hub
 * Unified view of all contacts with timeline, email, and lead scoring
 */

import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Badge, Modal, Form, Row, Col, Alert, Spinner, InputGroup
} from 'react-bootstrap';
import {
  FaUsers, FaSearch, FaEnvelope, FaPaperPlane, FaStar, FaCalendarAlt,
  FaEdit, FaEye, FaSync, FaChartLine, FaFire, FaUserPlus
} from 'react-icons/fa';
import axiosInstance from '../../helpers/api';

interface Contact {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  brokerage: string;
  source: string;
  lead_score: number;
  status: string;
  ai_summary: string | null;
  last_activity_at: string | null;
  created_at: string;
  activity_count: number;
  emails_sent: number;
}

interface Activity {
  id: string;
  activity_type: string;
  activity_data: any;
  created_at: string;
}

interface EmailRecord {
  id: string;
  subject: string;
  body: string;
  template: string;
  status: string;
  sent_at: string;
}

interface EventReg {
  event_title: string;
  event_date: string;
  event_time: string;
  attended: boolean;
  status: string;
}

interface Stats {
  total: number;
  hot: number;
  engaged: number;
  new_contacts: number;
  converted: number;
  avg_score: number;
}

const ContactsHub: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showBulkEmail, setShowBulkEmail] = useState(false);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [emailHistory, setEmailHistory] = useState<EmailRecord[]>([]);
  const [contactEvents, setContactEvents] = useState<EventReg[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [emailData, setEmailData] = useState({ subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [alert, setAlert] = useState<{ type: string; message: string } | null>(null);
  const [noteText, setNoteText] = useState('');

  useEffect(() => { fetchContacts(); }, [search, filterStatus]);

  const fetchContacts = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filterStatus !== 'all') params.set('status', filterStatus);
      const response = await axiosInstance.get(`/contacts?${params.toString()}`);
      if (response.data.success) {
        setContacts(response.data.contacts);
        setStats(response.data.stats);
      }
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  const openContact = async (contact: Contact) => {
    setSelectedContact(contact);
    setShowDetail(true);
    setDetailLoading(true);
    try {
      const response = await axiosInstance.get(`/contacts/${contact.id}`);
      if (response.data.success) {
        setActivities(response.data.activities);
        setEmailHistory(response.data.emails);
        setContactEvents(response.data.events);
      }
    } catch (err) {
      console.error('Failed to fetch contact detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleStatusChange = async (contactId: string, newStatus: string) => {
    try {
      await axiosInstance.put(`/contacts/${contactId}`, { status: newStatus });
      fetchContacts();
    } catch (err) {
      setAlert({ type: 'danger', message: 'Failed to update status' });
    }
  };

  const handleAddNote = async () => {
    if (!selectedContact || !noteText.trim()) return;
    try {
      await axiosInstance.put(`/contacts/${selectedContact.id}`, { notes: noteText });
      setNoteText('');
      openContact(selectedContact); // Refresh timeline
      setAlert({ type: 'success', message: 'Note added' });
    } catch (err) {
      setAlert({ type: 'danger', message: 'Failed to add note' });
    }
  };

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedContact) return;
    setSending(true);
    try {
      await axiosInstance.post(`/contacts/${selectedContact.id}/email`, {
        subject: emailData.subject,
        message: emailData.message
      });
      setAlert({ type: 'success', message: `Email sent to ${selectedContact.full_name}` });
      setShowEmailModal(false);
      setEmailData({ subject: '', message: '' });
      openContact(selectedContact);
    } catch (err: any) {
      setAlert({ type: 'danger', message: err.response?.data?.error || 'Failed to send' });
    } finally {
      setSending(false);
    }
  };

  const handleBulkEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    try {
      const response = await axiosInstance.post('/contacts/bulk-email', {
        subject: emailData.subject,
        message: emailData.message,
        status: filterStatus !== 'all' ? filterStatus : undefined
      });
      setAlert({ type: 'success', message: `Email sent to ${response.data.sent} contacts` });
      setShowBulkEmail(false);
      setEmailData({ subject: '', message: '' });
    } catch (err: any) {
      setAlert({ type: 'danger', message: err.response?.data?.error || 'Failed to send' });
    } finally {
      setSending(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await axiosInstance.post('/contacts/sync');
      setAlert({ type: 'success', message: response.data.message });
      fetchContacts();
    } catch (err) {
      setAlert({ type: 'danger', message: 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = { new: 'secondary', engaged: 'primary', hot: 'danger', converted: 'success', inactive: 'dark' };
    return <Badge bg={colors[status] || 'secondary'}>{status}</Badge>;
  };

  const getScoreColor = (score: number) => {
    if (score >= 50) return '#e74c3c';
    if (score >= 30) return '#f39c12';
    if (score >= 10) return '#3498db';
    return '#95a5a6';
  };

  const getActivityIcon = (type: string) => {
    const icons: Record<string, string> = {
      event_registered: '📋', event_attended: '✅', email_sent: '📧',
      email_opened: '👁️', note_added: '📝', synced_from_events: '🔄',
      portal_signup: '🚀', cma_viewed: '📊'
    };
    return icons[type] || '•';
  };

  const formatActivityData = (type: string, data: any) => {
    const parsed = typeof data === 'string' ? JSON.parse(data) : data;
    switch (type) {
      case 'note_added': return parsed?.note || 'Note added';
      case 'email_sent': return `Email: "${parsed?.subject || 'No subject'}"`;
      case 'synced_from_events': return `Synced: ${parsed?.events_registered || 0} events, ${parsed?.events_attended || 0} attended`;
      default: return type.replace(/_/g, ' ');
    }
  };

  return (
    <div>
      {alert && (
        <Alert variant={alert.type} dismissible onClose={() => setAlert(null)} className="mb-3">
          {alert.message}
        </Alert>
      )}

      {/* Stats Cards */}
      {stats && (
        <Row className="g-3 mb-3">
          {[
            { label: 'Total Contacts', value: Number(stats.total), icon: FaUsers, color: '#3498db' },
            { label: 'Hot Leads', value: Number(stats.hot), icon: FaFire, color: '#e74c3c' },
            { label: 'Engaged', value: Number(stats.engaged), icon: FaChartLine, color: '#f39c12' },
            { label: 'Converted', value: Number(stats.converted), icon: FaStar, color: '#27ae60' }
          ].map((s, i) => (
            <Col md={3} key={i}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="d-flex align-items-center">
                  <div style={{ background: s.color, color: 'white', width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 12 }}>
                    <s.icon />
                  </div>
                  <div>
                    <div style={{ fontSize: '24px', fontWeight: 700 }}>{s.value}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{s.label}</div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Contacts Table */}
      <Card className="mb-3">
        <Card.Header>
          <Row className="align-items-center">
            <Col md={4}>
              <h5 className="mb-0"><FaUsers className="me-2" />Contacts</h5>
            </Col>
            <Col md={3}>
              <InputGroup size="sm">
                <InputGroup.Text><FaSearch /></InputGroup.Text>
                <Form.Control placeholder="Search name, email, brokerage..." value={search} onChange={e => setSearch(e.target.value)} />
              </InputGroup>
            </Col>
            <Col md={2}>
              <Form.Select size="sm" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <option value="all">All Status</option>
                <option value="new">New</option>
                <option value="engaged">Engaged</option>
                <option value="hot">Hot</option>
                <option value="converted">Converted</option>
                <option value="inactive">Inactive</option>
              </Form.Select>
            </Col>
            <Col md={3} className="text-end">
              <Button variant="outline-primary" size="sm" className="me-1" onClick={handleSync} disabled={syncing}>
                <FaSync className={syncing ? 'fa-spin' : ''} /> {syncing ? 'Syncing...' : 'Sync'}
              </Button>
              <Button variant="primary" size="sm" onClick={() => { setEmailData({ subject: '', message: '' }); setShowBulkEmail(true); }}>
                <FaEnvelope className="me-1" /> Email All
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body style={{ maxHeight: '600px', overflowY: 'auto' }}>
          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : contacts.length === 0 ? (
            <p className="text-muted text-center py-4">No contacts found. Click "Sync" to import from event registrations.</p>
          ) : (
            <Table responsive hover size="sm">
              <thead>
                <tr>
                  <th style={{ minWidth: '180px' }}>Name</th>
                  <th>Email</th>
                  <th>Brokerage</th>
                  <th>Score</th>
                  <th>Status</th>
                  <th>Last Activity</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map(contact => (
                  <tr key={contact.id}>
                    <td>
                      <strong style={{ cursor: 'pointer', color: '#0d6efd' }} onClick={() => openContact(contact)}>
                        {contact.full_name}
                      </strong>
                      {contact.phone && <><br /><small className="text-muted">{contact.phone}</small></>}
                    </td>
                    <td><small><a href={`mailto:${contact.email}`}>{contact.email}</a></small></td>
                    <td><small>{contact.brokerage || '—'}</small></td>
                    <td>
                      <span style={{ fontWeight: 700, color: getScoreColor(contact.lead_score) }}>
                        {contact.lead_score}
                      </span>
                    </td>
                    <td>
                      <Form.Select size="sm" value={contact.status} onChange={e => handleStatusChange(contact.id, e.target.value)} style={{ fontSize: '11px', width: '100px' }}>
                        <option value="new">New</option>
                        <option value="engaged">Engaged</option>
                        <option value="hot">Hot</option>
                        <option value="converted">Converted</option>
                        <option value="inactive">Inactive</option>
                      </Form.Select>
                    </td>
                    <td><small className="text-muted">{contact.last_activity_at ? new Date(contact.last_activity_at).toLocaleDateString() : '—'}</small></td>
                    <td>
                      <Button variant="outline-primary" size="sm" className="me-1" title="View" onClick={() => openContact(contact)}>
                        <FaEye />
                      </Button>
                      <Button variant="outline-success" size="sm" title="Email" onClick={() => { setSelectedContact(contact); setEmailData({ subject: '', message: '' }); setShowEmailModal(true); }}>
                        <FaEnvelope />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Contact Detail Modal */}
      <Modal show={showDetail} onHide={() => setShowDetail(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedContact?.full_name}
            <span className="ms-2">{selectedContact && getStatusBadge(selectedContact.status)}</span>
            <span className="ms-2" style={{ color: getScoreColor(selectedContact?.lead_score || 0), fontSize: '16px' }}>
              Score: {selectedContact?.lead_score}
            </span>
            <br />
            <small className="text-muted">{selectedContact?.email} | {selectedContact?.phone || 'No phone'} | {selectedContact?.brokerage || 'No brokerage'}</small>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailLoading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : (
            <Row>
              {/* Left: Timeline */}
              <Col md={7}>
                <h6><FaCalendarAlt className="me-2" />Activity Timeline</h6>

                {/* Events */}
                {contactEvents.length > 0 && (
                  <Card className="mb-3 border-0 bg-light">
                    <Card.Body>
                      <strong>Events</strong>
                      {contactEvents.map((ev, i) => (
                        <div key={i} className="d-flex justify-content-between align-items-center py-1" style={{ fontSize: '13px' }}>
                          <span>{ev.attended ? '✅' : '📋'} {ev.event_title}</span>
                          <Badge bg={ev.attended ? 'success' : 'secondary'} style={{ fontSize: '10px' }}>
                            {ev.attended ? 'Attended' : ev.status}
                          </Badge>
                        </div>
                      ))}
                    </Card.Body>
                  </Card>
                )}

                {/* Activity Log */}
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {activities.map(act => (
                    <div key={act.id} className="d-flex py-2" style={{ borderBottom: '1px solid #eee', fontSize: '13px' }}>
                      <span className="me-2">{getActivityIcon(act.activity_type)}</span>
                      <div>
                        <span>{formatActivityData(act.activity_type, act.activity_data)}</span>
                        <br />
                        <small className="text-muted">{new Date(act.created_at).toLocaleString()}</small>
                      </div>
                    </div>
                  ))}
                  {activities.length === 0 && <p className="text-muted">No activity yet</p>}
                </div>

                {/* Add Note */}
                <div className="mt-3">
                  <InputGroup size="sm">
                    <Form.Control placeholder="Add a note..." value={noteText} onChange={e => setNoteText(e.target.value)} />
                    <Button variant="outline-primary" onClick={handleAddNote} disabled={!noteText.trim()}>Add Note</Button>
                  </InputGroup>
                </div>
              </Col>

              {/* Right: Email History */}
              <Col md={5}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0"><FaEnvelope className="me-2" />Email History</h6>
                  <Button variant="primary" size="sm" onClick={() => { setEmailData({ subject: '', message: '' }); setShowEmailModal(true); }}>
                    <FaPaperPlane className="me-1" /> Send Email
                  </Button>
                </div>
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {emailHistory.map(em => (
                    <Card key={em.id} className="mb-2 border-0 bg-light">
                      <Card.Body style={{ padding: '10px' }}>
                        <strong style={{ fontSize: '13px' }}>{em.subject}</strong>
                        <br />
                        <small className="text-muted">{new Date(em.sent_at).toLocaleString()} | {em.template}</small>
                      </Card.Body>
                    </Card>
                  ))}
                  {emailHistory.length === 0 && <p className="text-muted" style={{ fontSize: '13px' }}>No emails sent yet</p>}
                </div>
              </Col>
            </Row>
          )}
        </Modal.Body>
      </Modal>

      {/* Send Email Modal */}
      <Modal show={showEmailModal} onHide={() => setShowEmailModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Email {selectedContact?.full_name}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSendEmail}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Subject *</Form.Label>
              <Form.Control required value={emailData.subject} onChange={e => setEmailData({ ...emailData, subject: e.target.value })} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Message *</Form.Label>
              <Form.Control required as="textarea" rows={6} value={emailData.message} onChange={e => setEmailData({ ...emailData, message: e.target.value })}
                placeholder={"Hi {{name}},\n\n..."} />
              <Form.Text className="text-muted">Use <code>{'{{name}}'}</code> for personalization.</Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEmailModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={sending}>
              {sending ? <Spinner size="sm" /> : <><FaPaperPlane className="me-1" /> Send</>}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Bulk Email Modal */}
      <Modal show={showBulkEmail} onHide={() => setShowBulkEmail(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Email All Contacts {filterStatus !== 'all' ? `(${filterStatus})` : ''}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleBulkEmail}>
          <Modal.Body>
            <Alert variant="info">
              This will email {filterStatus !== 'all' ? `all "${filterStatus}" contacts` : 'all active contacts'}. Use <code>{'{{name}}'}</code> for personalization.
            </Alert>
            <Form.Group className="mb-3">
              <Form.Label>Subject *</Form.Label>
              <Form.Control required value={emailData.subject} onChange={e => setEmailData({ ...emailData, subject: e.target.value })} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Message *</Form.Label>
              <Form.Control required as="textarea" rows={8} value={emailData.message} onChange={e => setEmailData({ ...emailData, message: e.target.value })} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowBulkEmail(false)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={sending}>
              {sending ? <><Spinner size="sm" className="me-1" />Sending...</> : <><FaPaperPlane className="me-1" /> Send to All</>}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default ContactsHub;
